import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLingui } from "@lingui/react/macro";
import {
  XCircle,
  UserPlus,
  Search,
  ArrowUpDown,
  SlidersHorizontal,
  LayoutList,
  LayoutGrid,
  Timer,
  ChevronDown,
  Inbox,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/api/api";
import { StatusPill as UiStatusPill } from "@/components/ui/status-pill";
import { TimeChip } from "@/components/ui/time-chip";
import { WorkingTablePagination } from "@/components/data-table/working-table-pagination";
import { useCookingSessions } from "@/hooks/cooking-sessions/queries";
import { useBulkActionCookingSessions, useUpdateCookingSession, useDeleteCookingSession } from "@/hooks/cooking-sessions/mutations";
import { AssignChefDialog, type StationUndo } from "./AssignChefDialog";
import { AssigneeBadge } from "./AssigneeBadge";
import { AssigneeFilterOptions } from "./AssigneeFilterOptions";
import { type AssigneeFilter, assigneeFilterParams } from "./assignee-filter";
import { BulkActionsBar } from "./BulkActionsBar";
import { useSelectionSlot } from "./multi-view/selection-store";
import { BulkActionConfirmBar } from "./BulkActionConfirmBar";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import type { BulkSessionAction, CookingSession, CookingSessionSource, DeckPriority } from "@/types/cooking-session";

const PAGE_SIZE = 5;

// ---------------------------------------------------------------------------
// Priority pill
// ---------------------------------------------------------------------------

const PRIORITY_STATUS = { HIGH: "high", NORMAL: "normal", LOW: "low" } as const;

function PriorityPill({ priority }: { priority: DeckPriority }) {
  const { t } = useLingui();
  const PRIORITY_LABEL: Record<DeckPriority, string> = {
    HIGH:   t`High`,
    NORMAL: t`Normal`,
    LOW:    t`Low`,
  };
  return (
    <UiStatusPill variant="solid" status={PRIORITY_STATUS[priority]}>
      {PRIORITY_LABEL[priority]}
    </UiStatusPill>
  );
}

// ---------------------------------------------------------------------------
// ETA pill
// ---------------------------------------------------------------------------

function EtaPill({ minutes }: { minutes: number }) {
  return (
    <TimeChip icon={<Timer className="h-4.5 w-4.5 shrink-0 text-(--color-text-secondary)" />}>
      {minutes}m
    </TimeChip>
  );
}

// ---------------------------------------------------------------------------
// Assign chef modal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Chef assigned toast
// ---------------------------------------------------------------------------

interface ToastState {
  sessionId: number;
  previousChefId: number | null;
  /** Who was assigned: a chef, a station, or both (YC-36). */
  chefName: string;
  stationUndo?: StationUndo;
}

function ChefAssignedToast({
  chefName,
  canUndo,
  onUndo,
  onDismiss,
}: {
  chefName: string;
  canUndo: boolean;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  const { t } = useLingui();

  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-(--color-action) rounded-xl px-4 py-3 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200">
      <CheckCircle className="h-5 w-5 shrink-0 text-(--color-action-fg)/80" />
      <span className="text-base text-(--color-action-fg)/80 whitespace-nowrap">
        {chefName ? t`${chefName} has been assigned.` : t`Chef has been assigned.`}
      </span>
      {canUndo && (
        <>
          <div className="w-px h-5 bg-white/20 shrink-0" />
          <Button
            variant="ghost"
            onClick={onUndo}
            className="h-auto px-0 text-base font-medium text-white hover:bg-transparent hover:text-white/70 whitespace-nowrap"
          >
            {t`Undo`}
          </Button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chef display — shared between row and card
// ---------------------------------------------------------------------------

function ChefDisplay({ chefName, stationName, canAssign, onAssign }: { chefName: string | null; stationName: string | null; canAssign: boolean; onAssign: () => void }) {
  const { t } = useLingui();
  if (chefName || stationName) {
    return (
      <div className="flex flex-col gap-0.5">
        <AssigneeBadge chefName={chefName} stationName={stationName} />
        {canAssign && (
          <Button
            variant="link"
            onClick={onAssign}
            className="pl-10 h-auto p-0 text-xs text-(--color-text-secondary) hover:text-(--color-text-primary)"
          >
            {t`Reassign`}
          </Button>
        )}
      </div>
    );
  }
  if (canAssign) {
    return (
      <Button variant="link" onClick={onAssign} className="h-auto gap-1.5 p-0 text-sm">
        <UserPlus className="h-4 w-4 shrink-0" />
        {t`Assign`}
        <ChevronDown className="h-3 w-3" />
      </Button>
    );
  }
  return <span className="text-sm text-(--color-text-secondary)">{t`Unassigned`}</span>;
}

// ---------------------------------------------------------------------------
// Column layout — CSS Grid keeps header and rows pixel-aligned
// ---------------------------------------------------------------------------

/**
 * Shared grid template used by both the header and every data row. Three tiers,
 * switched by the width of the table's own container (`@container` on the list
 * wrapper) rather than the viewport, so the sidebar being shown or hidden is
 * accounted for (YC-32).
 *
 * Wide (container >= 62.5rem / 1000px, the previous minimum width):
 *   checkbox 3.125rem | recipe 1fr | allergens 8.75rem | chef 12.5rem |
 *   ETA 7.5rem | priority 6.25rem | actions 13.25rem (Remove + gap + Start)
 * Compact (50rem to 62.5rem, e.g. iPad landscape): same seven columns, narrower.
 * Narrow (below 50rem, e.g. iPad portrait): Allergens moves under the recipe
 * name (see ALLERGENS_CELL / the recipe sub-line) and Remove shrinks to an icon.
 */
const TABLE_GRID =
  "grid-cols-[2.5rem_minmax(7rem,1fr)_8rem_5rem_5.5rem_9.75rem] " +
  "@min-[50rem]:grid-cols-[2.5rem_minmax(5.5rem,1fr)_5.5rem_9rem_5.5rem_6rem_13.25rem] " +
  "@min-[62.5rem]:grid-cols-[3.125rem_1fr_8.75rem_12.5rem_7.5rem_6.25rem_13.25rem]";

/** The Allergens column only exists from the compact tier up. */
const ALLERGENS_CELL = "hidden @min-[50rem]:block";

const COLS = {
  checkbox:  "w-[3.125rem] shrink-0",
  recipe:    "flex-1 min-w-[12.5rem]",
  allergens: "w-[8.75rem] shrink-0",
  chef:      "w-[12.5rem] shrink-0",
  eta:       "w-[7.5rem] shrink-0",
  priority:  "w-[6.25rem] shrink-0",
  actions:   "shrink-0",
};

function TableHeader({ allSelected, someSelected, onToggleAll }: {
  allSelected: boolean; someSelected: boolean; onToggleAll: (v: boolean) => void;
}) {
  const { t } = useLingui();
  return (
    <div
      className={cn("grid items-center w-full px-3 border-b border-(--color-border) mb-1", TABLE_GRID)}
    >
      {/* Checkbox */}
      <div className="py-3">
        <Checkbox
          checked={allSelected || (someSelected && "indeterminate")}
          onCheckedChange={(v) => onToggleAll(v === true)}
          aria-label={t`Select all`}
        />
      </div>
      {/* Column labels */}
      {[t`Recipe`, t`Allergens`, t`Chef`, t`ETA`, t`Priority`, t`Actions`].map((label, i) => (
        <div key={label} className={cn("py-3 px-2 text-sm font-medium text-(--color-text-secondary) last:text-right", i === 1 && ALLERGENS_CELL)}>
          {label}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data row
// ---------------------------------------------------------------------------

function DeckRow({
  row, selected, onSelect, onStart, onRemove, onAssigned, canCook, canAssign, canDelete,
}: {
  row: CookingSession;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onStart: (id: number) => Promise<void>;
  onRemove: (id: number) => Promise<void>;
  onAssigned: (sessionId: number, previousChefId: number | null, chefName: string, stationUndo?: StationUndo) => void;
  canCook: boolean;
  canAssign: boolean;
  canDelete: boolean;
}) {
  const { t } = useLingui();
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pending, setPending] = useState<"start" | null>(null);
  return (
    <>
    <div
      data-testid="deck-row"
      className={cn("grid items-center w-full bg-(--color-surface-muted) rounded-xl px-3 mb-1.5", TABLE_GRID)}
    >
      {/* Checkbox */}
      <div className="py-4 flex items-center">
        <Checkbox checked={selected} onCheckedChange={(v) => onSelect(v === true)} aria-label={t`Select row`} className="bg-(--color-surface-content) border-(--color-border)" />
      </div>

      {/* Recipe */}
      <div className="py-3 px-2 min-w-0">
        <p className="text-sm font-medium text-(--color-text-primary) truncate">{row.recipeName}</p>
        <p className="text-xs text-(--color-text-secondary)">{t`Queued · Waiting`}</p>
        {row.orderAllergens.length > 0 && (
          <p className="text-xs text-(--color-text-secondary) break-words @min-[50rem]:hidden">
            {t`Allergens:`} {row.orderAllergens.join(', ')}
          </p>
        )}
      </div>

      {/* Allergens */}
      <div className={cn("py-4 px-2", ALLERGENS_CELL)}>
        <span className="text-base font-semibold text-(--color-text-secondary) break-words">
          {row.orderAllergens.length > 0 ? row.orderAllergens.join(', ') : "—"}
        </span>
      </div>

      {/* Chef */}
      <div className={cn(COLS.chef, "py-3 px-2")}>
        <ChefDisplay chefName={row.chefName} stationName={row.stationName} canAssign={canAssign} onAssign={() => setAssignOpen(true)} />
      </div>

      {/* ETA */}
      <div className="py-3 px-2">
        <EtaPill minutes={row.prepTimeMinutes} />
      </div>

      {/* Priority */}
      <div className="py-3 px-2">
        <PriorityPill priority={row.priority} />
      </div>

      {/* Actions */}
      <div className="py-3 flex items-center justify-end gap-1.5">
        {canDelete && (
          <Button
            variant="destructive-subtle"
            disabled={pending !== null}
            onClick={() => setDeleteOpen(true)}
            className="h-11 rounded-xl px-3 gap-2 @max-[50rem]:w-11 @max-[50rem]:px-0 @max-[50rem]:gap-0"
          >
            <XCircle className="h-4 w-4 shrink-0" />
            {/* Icon-only in the narrow tier; the label stays for screen readers */}
            <span className="@max-[50rem]:sr-only">{t`Remove`}</span>
          </Button>
        )}
        {canCook && (
          <Button
            variant="default"
            disabled={pending !== null}
            onClick={async () => {
              setPending("start");
              try { await onStart(row.id); } finally { setPending(null); }
            }}
            className="h-11 w-26.5 rounded-xl px-2 gap-2"
          >
            {pending === "start"
              ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              : <Timer className="h-4 w-4 shrink-0" />}
            {pending === "start" ? t`Starting...` : t`Start`}
          </Button>
        )}
      </div>
    </div>
    <AssignChefDialog open={assignOpen} onOpenChange={setAssignOpen} sessionId={row.id} previousChefId={row.chefId} locationId={row.locationId} previousStationId={row.stationId} onAssigned={onAssigned} />
    <DeleteConfirmDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={t`Remove session`}
      description={t`Remove "${row.recipeName}" from the deck? This action cannot be undone.`}
      onConfirm={() => onRemove(row.id)}
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// Card (grid view)
// ---------------------------------------------------------------------------

function DeckCard({
  row, selected, onSelect, onStart, onRemove, onAssigned, canCook, canAssign, canDelete,
}: {
  row: CookingSession;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onStart: (id: number) => Promise<void>;
  onRemove: (id: number) => Promise<void>;
  onAssigned: (sessionId: number, previousChefId: number | null, chefName: string, stationUndo?: StationUndo) => void;
  canCook: boolean;
  canAssign: boolean;
  canDelete: boolean;
}) {
  const { t } = useLingui();
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pending, setPending] = useState<"start" | null>(null);

  return (
    <>
    <div className="flex flex-col gap-4 bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4">
      <div className="flex gap-3 items-start">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <EtaPill minutes={row.prepTimeMinutes} />
            <PriorityPill priority={row.priority} />
          </div>
          <p className="text-base font-medium text-(--color-text-primary) truncate">{row.recipeName}</p>
          <p className="text-xs text-(--color-text-secondary)">{t`Queued · Waiting`}</p>
        </div>
        <div className="flex items-start p-2 shrink-0">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelect(v === true)}
            aria-label={t`Select`}
            className="bg-(--color-surface-content) border-(--color-border)"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between bg-(--color-surface-content) rounded-xl px-3 py-3">
          <span className="text-sm font-medium text-(--color-text-primary)">{t`Allergens:`}</span>
          <span className="text-base font-semibold text-(--color-text-secondary)">
            {row.orderAllergens.length > 0 ? row.orderAllergens.join(', ') : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between bg-(--color-surface-content) rounded-xl px-3 py-3">
          <span className="text-sm font-medium text-(--color-text-primary)">{t`Chef:`}</span>
          <ChefDisplay chefName={row.chefName} stationName={row.stationName} canAssign={canAssign} onAssign={() => setAssignOpen(true)} />
        </div>
      </div>

      <div className="border-t border-(--color-border)" />

      <div className="flex items-center gap-1.5">
        {canDelete && (
          <Button
            variant="destructive-subtle"
            disabled={pending !== null}
            onClick={() => setDeleteOpen(true)}
            className="h-11 flex-1 rounded-xl gap-2"
          >
            <XCircle className="h-4 w-4 shrink-0" />
            {t`Remove`}
          </Button>
        )}
        {canCook && (
          <Button
            variant="default"
            disabled={pending !== null}
            onClick={async () => {
              setPending("start");
              try { await onStart(row.id); } finally { setPending(null); }
            }}
            className="h-11 flex-1 rounded-xl gap-2"
          >
            {pending === "start"
              ? <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              : <Timer className="h-4 w-4 shrink-0" />}
            {pending === "start" ? t`Starting...` : t`Start`}
          </Button>
        )}
      </div>
    </div>
    <AssignChefDialog open={assignOpen} onOpenChange={setAssignOpen} sessionId={row.id} previousChefId={row.chefId} locationId={row.locationId} previousStationId={row.stationId} onAssigned={onAssigned} />
    <DeleteConfirmDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={t`Remove session`}
      description={t`Remove "${row.recipeName}" from the deck? This action cannot be undone.`}
      onConfirm={() => onRemove(row.id)}
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sort + filter options
// ---------------------------------------------------------------------------

type AssignedFilter = "all" | "assigned" | "unassigned";
type PriorityFilter = "all" | DeckPriority;

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface OnDeckSectionProps {
  locationId?: number;
  source?: CookingSessionSource;
}

export function OnDeckSection({ locationId, source }: OnDeckSectionProps) {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const canCook = hasPermission("cooking-session:cook");
  const canAssign = hasPermission("cooking-session:assign");
  const canDelete = hasPermission("cooking-session:delete");

  const SORT_OPTIONS = [
    { label: t`ETA (shortest first)`,  sortBy: "targetMinutes", sortOrder: "asc"  as const },
    { label: t`ETA (longest first)`,   sortBy: "targetMinutes", sortOrder: "desc" as const },
    { label: t`Priority (high first)`, sortBy: "priority",      sortOrder: "asc"  as const },
    { label: t`Recipe name`,           sortBy: "recipeName",    sortOrder: "asc"  as const },
  ];

  // On Prep the ticks are shared with Multi View (YC-23); on the service board they stay local.
  const [selected, setSelected] = useSelectionSlot("onDeck", source === "prep");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>("all");
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>(null);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [assignedToast, setAssignedToast] = useState<ToastState | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState<{
    ids: number[];
    action: BulkSessionAction;
    inverseAction: BulkSessionAction | null;
    /** Who was on each recipe before the start, so Undo can put it back. */
    previousChefIds: Record<number, number | null>;
  } | null>(null);

  const updateSession = useUpdateCookingSession();
  const deleteSession = useDeleteCookingSession();
  const bulkAction = useBulkActionCookingSessions();

  const { data, isLoading } = useCookingSessions({
    locationId,
    source,
    status: "ON_DECK",
    search: search || undefined,
    sortBy,
    sortOrder,
    page,
    limit: PAGE_SIZE,
    priority: priorityFilter !== "all" ? priorityFilter : undefined,
    assigned: assignedFilter === "assigned" ? true : assignedFilter === "unassigned" ? false : undefined,
    ...assigneeFilterParams(assigneeFilter),
  });

  const sessions = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const allSelected = sessions.length > 0 && sessions.every((r) => selected[r.id]);
  const someSelected = sessions.some((r) => selected[r.id]);
  const selectedIds = sessions.filter((r) => selected[r.id]).map((r) => r.id);

  function handleSearch(v: string) { setSearch(v); setPage(1); }
  function toggleAll(v: boolean) {
    setSelected(v ? Object.fromEntries(sessions.map((r) => [r.id, true])) : {});
  }
  function chefsBefore(ids: number[]): Record<number, number | null> {
    return Object.fromEntries(ids.map((id) => [id, sessions.find((r) => r.id === id)?.chefId ?? null]));
  }
  async function handleStart(id: number): Promise<void> {
    const previousChefIds = chefsBefore([id]);
    await updateSession.mutateAsync({ id, data: { status: "COOKING" } });
    setBulkConfirm({ ids: [id], action: "COOKING", inverseAction: "ON_DECK", previousChefIds });
  }
  async function handleRemove(id: number): Promise<void> {
    await deleteSession.mutateAsync(id);
  }
  const ON_DECK_UNDO: Partial<Record<BulkSessionAction, BulkSessionAction>> = {
    COOKING: "ON_DECK",
  };

  async function handleBulkAction(action: BulkSessionAction): Promise<void> {
    const ids = [...selectedIds];
    const previousChefIds = chefsBefore(ids);
    await bulkAction.mutateAsync({ ids, action });
    setSelected({});
    setBulkConfirm({ ids, action, inverseAction: ON_DECK_UNDO[action] ?? null, previousChefIds });
  }

  // Undo of a start. Starting hands an unassigned recipe to whoever started it,
  // so putting it back on the deck also has to take that chef off again; a
  // recipe that already had a chef keeps them either way.
  const handleBulkUndo = useCallback(async () => {
    if (!bulkConfirm?.inverseAction) return;
    const { ids, previousChefIds } = bulkConfirm;
    await Promise.all(
      ids.map((id) =>
        updateSession.mutateAsync({
          id,
          data: { status: "ON_DECK", ...(previousChefIds[id] === null && { chefId: null }) },
        }),
      ),
    );
    setBulkConfirm(null);
  }, [bulkConfirm, updateSession]);

  const handleAssigned = useCallback((sessionId: number, previousChefId: number | null, chefName: string, stationUndo?: StationUndo) => {
    setAssignedToast({ sessionId, previousChefId, chefName, stationUndo });
  }, []);

  function handleUndo() {
    if (!assignedToast) return;
    const { previousChefId, stationUndo } = assignedToast;
    if (previousChefId !== null || stationUndo) {
      // Same reason as the dialog (T-1102): an undo that quietly failed left
      // the board showing the new chef and the toast gone.
      updateSession.mutate(
        {
          id: assignedToast.sessionId,
          data: {
            ...(previousChefId !== null && { chefId: previousChefId }),
            ...(stationUndo && { stationId: stationUndo.previousStationId }),
          },
        },
        { onError: (error) => toast.error(getApiErrorMessage(error, t`Could not undo the assignment`)) },
      );
    }
    setAssignedToast(null);
  }

  const hasActiveFilter = priorityFilter !== "all" || assignedFilter !== "all" || assigneeFilter !== null;

  return (
    <div className="flex flex-col px-3 pb-5" data-testid="on-deck-section">
      {/* Section title */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-(--color-text-primary)">{t`On deck`}</span>
          <Badge variant="neutral" className="rounded-full text-xs">
            {isLoading ? "…" : t`${total} pending`}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setViewMode("list")} className={cn("h-9 w-9", viewMode === "list" && "bg-(--color-surface-muted)")}><LayoutList className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setViewMode("grid")} className={cn("h-9 w-9", viewMode === "grid" && "bg-(--color-surface-muted)")}><LayoutGrid className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between pb-3">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-(--color-text-secondary)" />
          <Input placeholder={t`Search...`} value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-9 h-11" />
        </div>
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <div className="relative">
            <Button variant="brand-outline" size="sm" className="h-11 gap-2" onClick={() => { setShowSort(!showSort); setShowFilter(false); }}>
              <ArrowUpDown className="h-4 w-4" />
              {t`Sort`}
            </Button>
            {showSort && (
              <div className="absolute right-0 top-12 z-10 w-52 rounded-lg border border-(--color-border) bg-(--color-surface-page) shadow-md">
                {SORT_OPTIONS.map((opt) => (
                  <Button key={opt.label} variant="ghost"
                    onClick={() => { setSortBy(opt.sortBy); setSortOrder(opt.sortOrder); setShowSort(false); }}
                    className={cn("w-full justify-start px-3 py-2.5 h-auto text-sm",
                      sortBy === opt.sortBy && sortOrder === opt.sortOrder && "font-medium text-(--color-action)")}>
                    {opt.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Filter dropdown */}
          <div className="relative">
            <Button variant="brand-outline" size="sm" className="h-11 gap-2" onClick={() => { setShowFilter(!showFilter); setShowSort(false); }}>
              <SlidersHorizontal className="h-4 w-4" />
              {t`Filter`}
              {hasActiveFilter && <span className="ml-1 size-2 rounded-full bg-(--color-action)" />}
            </Button>
            {showFilter && (
              <div className="absolute right-0 top-12 z-10 w-52 max-h-96 overflow-y-auto rounded-lg border border-(--color-border) bg-(--color-surface-page) shadow-md p-2">
                <p className="px-2 pb-1 text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">{t`Priority`}</p>
                {(["all", "HIGH", "NORMAL", "LOW"] as PriorityFilter[]).map((p) => (
                  <Button key={p} variant="ghost"
                    onClick={() => { setPriorityFilter(p); setPage(1); }}
                    className={cn("w-full justify-start px-2 py-2 h-auto text-sm",
                      priorityFilter === p && "font-medium text-(--color-action)")}>
                    {p === "all" ? t`All` : p === "HIGH" ? t`High` : p === "NORMAL" ? t`Normal` : t`Low`}
                  </Button>
                ))}
                <div className="my-1 border-t border-(--color-border)" />
                <p className="px-2 pb-1 text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">{t`Assignment`}</p>
                {(["all", "assigned", "unassigned"] as AssignedFilter[]).map((a) => (
                  <Button key={a} variant="ghost"
                    onClick={() => { setAssignedFilter(a); setPage(1); }}
                    className={cn("w-full justify-start px-2 py-2 h-auto text-sm",
                      assignedFilter === a && "font-medium text-(--color-action)")}>
                    {a === "all" ? t`All` : a === "assigned" ? t`Assigned` : t`Unassigned`}
                  </Button>
                ))}
                <AssigneeFilterOptions
                  locationId={locationId}
                  value={assigneeFilter}
                  onChange={(next) => { setAssigneeFilter(next); setPage(1); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions — fixed floating bar at bottom-center */}
      <BulkActionsBar
        count={selectedIds.length}
        availableActions={[
          ...(canCook ? ["COOKING" as const] : []),
          ...(canCook ? ["COMPLETED" as const] : []),
          ...(canDelete ? ["DELETE" as const] : []),
        ]}
        onAction={handleBulkAction}
      />
      {bulkConfirm && (
        <BulkActionConfirmBar
          action={bulkConfirm.action}
          onUndo={bulkConfirm.inverseAction ? handleBulkUndo : undefined}
          onDismiss={() => setBulkConfirm(null)}
        />
      )}

      {/* Content */}
      {viewMode === "list" ? (
        <div className="@container overflow-x-auto rounded-xl border border-(--color-border)">
          {/* min-width is the grid total of the active tier (narrow ~650px, compact ~800px);
              below it the table scrolls inside this container instead of spilling out. */}
          <div className="min-w-162 @min-[50rem]:min-w-200">
            <TableHeader allSelected={allSelected} someSelected={someSelected} onToggleAll={toggleAll} />
            <div className="p-2">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-4 mb-1.5 bg-(--color-surface-muted) rounded-xl">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))
              ) : sessions.length === 0 ? (
                <div className="py-12 flex flex-col items-center gap-2 text-(--color-text-secondary)">
                  <Inbox className="h-8 w-8" />
                  <p className="text-sm">{t`The deck is empty.`}</p>
                </div>
              ) : (
                sessions.map((row) => (
                  <DeckRow
                    key={row.id}
                    row={row}
                    selected={!!selected[row.id]}
                    onSelect={(v) => setSelected((s) => ({ ...s, [row.id]: v }))}
                    onStart={handleStart}
                    onRemove={handleRemove}
                    onAssigned={handleAssigned}
                    canCook={canCook}
                    canAssign={canAssign}
                    canDelete={canDelete}
                  />
                ))
              )}
            </div>
            <div className="flex flex-col border-t border-(--color-border)">
              <div className="px-5 py-2">
                <span className="text-xs text-(--color-text-secondary)">
                  {t`${Object.values(selected).filter(Boolean).length} of ${total} row(s) selected.`}
                </span>
              </div>
              <WorkingTablePagination page={page} pageCount={pageCount} onPage={setPage} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-(--color-surface-muted) rounded-2xl p-3 flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-11 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-12 flex flex-col items-center gap-2 text-(--color-text-secondary)">
              <Inbox className="h-8 w-8" />
              <p className="text-sm">{t`The deck is empty.`}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {sessions.map((row) => (
                <DeckCard
                  key={row.id}
                  row={row}
                  selected={!!selected[row.id]}
                  onSelect={(v) => setSelected((s) => ({ ...s, [row.id]: v }))}
                  onStart={handleStart}
                  onRemove={handleRemove}
                  onAssigned={handleAssigned}
                  canCook={canCook}
                  canAssign={canAssign}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-(--color-text-secondary)">
              {t`${Object.values(selected).filter(Boolean).length} of ${total} row(s) selected.`}
            </span>
            <WorkingTablePagination page={page} pageCount={pageCount} onPage={setPage} />
          </div>
        </div>
      )}

      {assignedToast && (
        <ChefAssignedToast
          chefName={assignedToast.chefName}
          canUndo={assignedToast.previousChefId !== null || assignedToast.stationUndo !== undefined}
          onUndo={handleUndo}
          onDismiss={() => setAssignedToast(null)}
        />
      )}
    </div>
  );
}
